// =============================================================================
// Sincronizador de la cola offline. Toma las ventas 'pending' en orden FIFO y
// las envía a pos_finalize_sale. Como ese RPC es idempotente por client_uuid,
// reintentar tras una caída de red NUNCA duplica stock/pago/crédito.
// =============================================================================
import { db, type QueuedSale } from "./db";
import { pos, fiscal, ApiError } from "../api";

let running = false;

/** Procesa toda la cola pendiente. Seguro de llamar en cualquier momento. */
export async function syncQueue(): Promise<{ synced: number; failed: number }> {
  if (running || !navigator.onLine) return { synced: 0, failed: 0 };
  running = true;
  let synced = 0, failed = 0;
  try {
    const pending = await db.queuedSales
      .where("status").anyOf("pending", "error")
      .sortBy("createdAt");

    for (const sale of pending) {
      try {
        await db.queuedSales.update(sale.clientUuid, { status: "syncing" });
        await pushSale(sale);
        await db.queuedSales.update(sale.clientUuid, { status: "synced" });
        synced++;
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : String(e);
        await db.queuedSales.update(sale.clientUuid, {
          status: "error", attempts: sale.attempts + 1, lastError: msg,
        });
        failed++;
        // Un error de validación (no de red) no se arregla reintentando: se deja
        // marcado 'error' para revisión manual; no bloquea el resto de la cola.
      }
    }
  } finally {
    running = false;
  }
  return { synced, failed };
}

/**
 * Empuja UNA venta: reconstruye la venta remota (open + add_line) y la finaliza.
 * Si la venta ya existía (idempotencia), los RPC la reutilizan sin duplicar.
 */
async function pushSale(sale: QueuedSale): Promise<void> {
  // 1. Abrir/recuperar la venta por client_uuid (idempotente).
  const remote = await pos.openSale(sale.branchId, sale.clientUuid, {
    cashSessionId: sale.cashSessionId ?? undefined,
    customerId: sale.customerId ?? undefined,
  });

  // 2. Si la venta recién se abre (status 'open' sin líneas remotas), cargar líneas.
  //    Nota: si ya estaba 'paid' (reintento tras éxito parcial), finalizeSale
  //    devolverá la venta pagada sin reprocesar.
  if (remote.status === "open") {
    for (const ln of sale.lines) {
      // clientUuid hace add_line idempotente: si el sync reintenta, no duplica.
      await pos.addLine(remote.id, ln.productUnitId, ln.qty, ln.unitPrice, ln.discount ?? 0, ln.clientUuid, ln.lineNote);
    }
  }

  // 3. Finalizar (idempotente por client_uuid).
  const finalized = await pos.finalizeSale(remote.id, sale.clientUuid, sale.payments, sale.changeGiven);
  await db.queuedSales.update(sale.clientUuid, { saleId: finalized.id });

  // 4. Emitir factura electrónica si la venta lo pidió. Es idempotente por
  //    (sale_id, doc_type): reintentar no duplica el documento. Un fallo del PAC
  //    NO revierte la venta (vender ≠ facturar); queda en cola fiscal y el cron
  //    fiscal-reconcile lo reintenta.
  if (sale.emitInvoice) {
    try {
      await fiscal.enqueue(finalized.id, "factura_interna");
      await fiscal.emit(finalized.id, "factura_interna");
    } catch {
      // documento encolado; fiscal-reconcile lo retomará. No marca la venta error.
    }
  }
}

/** Arranca el auto-sync: al volver la red y cada 30s mientras haya pendientes. */
export function startAutoSync(): () => void {
  const onOnline = () => void syncQueue();
  window.addEventListener("online", onOnline);
  const timer = setInterval(() => void syncQueue(), 30_000);
  void syncQueue(); // intento inicial
  return () => {
    window.removeEventListener("online", onOnline);
    clearInterval(timer);
  };
}
